// File: src/ContactDetails.js
// Contact-details step: read-only email + phone number with country code.
//
// v3.13: Hoisted out of App.js, where it was declared inside the App component.
// A component declared inside another component is a NEW function object on
// every parent render, so React treats it as a different component type and
// unmounts/remounts the whole subtree instead of updating it. Its sibling
// InsuranceQuestionnaire lost the driver's answers that way; this one survived
// only because its phone input is uncontrolled (defaultValue) and re-seeds from
// App state on mount. Same latent bug, so it moves out too.
//
// Everything it needs now arrives as props — nothing is closed over.

import React from 'react';
import { StepShell, Card, PrimaryButton, Note, PHASE } from './ui/HireFormUI';

const ContactDetails = ({
  jobId,
  driverStatus,
  driverEmail,
  countryCode,
  onCountryChange,
  phoneNumber,
  onPhoneNumberChange,
  error,
  loading,
  onContinue,
}) => {
  return (
    <StepShell jobId={jobId} phase={PHASE.YOU}>
      <Card>
        <div>
          <h2 className="hf-h2">Your phone number</h2>
          <p className="hf-intro" style={{ marginTop: 6 }}>
            If anything comes up during the hire, this is how we'll reach you.
          </p>
        </div>

        <div className="hf-inset">
          <p className="hf-small">Email</p>
          <div className="hf-row" style={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <span className="hf-body hf-strong" style={{ wordBreak: 'break-word' }}>{driverEmail}</span>
            <span className="hf-pill">✓ Confirmed</span>
          </div>
        </div>

        <div className="hf-field">
          <label className="hf-label" htmlFor="phone">Mobile number</label>
          <div className="hf-row">
            <select
              value={countryCode}
              onChange={onCountryChange}
              className="hf-select"
              style={{ width: 112, flexShrink: 0 }}
              aria-label="Country code"
            >
              <option value="+44">UK +44</option>
              <option value="+1">US +1</option>
              <option value="+33">FR +33</option>
              <option value="+49">DE +49</option>
              <option value="+34">ES +34</option>
              <option value="+39">IT +39</option>
              <option value="+31">NL +31</option>
              <option value="+32">BE +32</option>
              <option value="+41">CH +41</option>
              <option value="+43">AT +43</option>
              <option value="+353">IE +353</option>
              <option value="+46">SE +46</option>
              <option value="+47">NO +47</option>
              <option value="+45">DK +45</option>
              <option value="+358">FI +358</option>
              <option value="+48">PL +48</option>
              <option value="+351">PT +351</option>
              <option value="+30">GR +30</option>
              <option value="+420">CZ +420</option>
              <option value="+36">HU +36</option>
              <option value="+61">AU +61</option>
              <option value="+64">NZ +64</option>
              <option value="+27">ZA +27</option>
              <option value="+91">IN +91</option>
              <option value="+86">CN +86</option>
              <option value="+81">JP +81</option>
              <option value="+82">KR +82</option>
              <option value="+55">BR +55</option>
              <option value="+52">MX +52</option>
            </select>

            {/* UNCONTROLLED on purpose, committed on blur (digits only). The
                Continue button below enables once the number is committed —
                the blur fires as the driver taps it. Re-seeds from App state
                on mount. Don't make this controlled or move the commit. */}
            <input
              id="phone"
              type="tel"
              defaultValue={phoneNumber}
              onBlur={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                onPhoneNumberChange(value);
                e.target.value = value;
              }}
              className="hf-input"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="7123 456789"
              autoComplete="tel-national"
            />
          </div>
        </div>

        {error && <Note tone="error">{error}</Note>}

        <PrimaryButton
          onClick={onContinue}
          disabled={!phoneNumber || phoneNumber.length < 7}
          busy={loading}
          busyLabel="Saving…"
        >
          Continue to insurance questions
        </PrimaryButton>
      </Card>
    </StepShell>
  );
};

export default ContactDetails;
